import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  XCircle,
  Minus,
  ArrowRight,
} from "lucide-react";
import { SeoPageFooter } from "@/components/seo-page-footer";

const PAGE_URL = "https://thepropertool.com/tradify-alternative";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | Tradify Alternative",
  "description": "Looking for a Tradify alternative? Compare The Proper Tool vs Tradify on Smart Quoting, UK pricing, drawing analysis, and cost. Start free for 14 days.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Tradify Alternative", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the best Tradify alternative?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The best Tradify alternative depends on what you need. If your main requirement is faster, more accurate quoting from drawings or descriptions, rather than job management and scheduling, The Proper Tool is the better fit. It uses AI to generate fully itemised quotes from drawings or a plain description, includes a 31-category UK cost library, and starts at £29/month with a 14-day free trial.",
      },
    },
    {
      "@type": "Question",
      "name": "Is The Proper Tool better than Tradify for UK builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For quoting and estimating specifically, yes. The Proper Tool is built around the UK residential construction market with a 31-category UK cost library, AI quote generation from drawings and descriptions, structural drawing analysis, and expert scope validation. Tradify has a broader feature set including job scheduling and invoicing, but its quoting module is more basic with no AI scope generation or drawing analysis.",
      },
    },
    {
      "@type": "Question",
      "name": "Does Tradify read structural drawings?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. Tradify does not analyse structural drawings or architect's plans. The Proper Tool can read uploaded PDFs of drawings, extracting beam sizes, spans, floor areas, and dimensions to generate a more accurate quote. This is a key differentiator for builders who work from architect's drawings on extension and conversion projects.",
      },
    },
    {
      "@type": "Question",
      "name": "How does Tradify pricing compare to The Proper Tool?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Tradify lists Lite at £34, Pro at £37, and Plus at £44 per user/month, with a 14-day free trial. Tradify supports iPhone, iPad, Android and web. The Proper Tool is priced per account, starts at £29/month and offers a 14-day free trial.",
      },
    },
    {
      "@type": "Question",
      "name": "Can I switch from Tradify to The Proper Tool?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool has a 14-day free trial with no credit card required, so you can run it alongside your existing tools before committing. Setup takes minutes with no long onboarding or data migration required to start generating quotes.",
      },
    },
  ],
};

type TriState = "yes" | "partial" | "no";

interface ComparisonRow {
  feature: string;
  tradify: string;
  tradifyState: TriState;
  tpt: string;
  tptState: TriState;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  { feature: "AI quote generation from description", tradify: "No evidence", tradifyState: "partial", tpt: "Yes", tptState: "yes" },
  { feature: "Reads structural drawings and plans", tradify: "No drawing takeoff", tradifyState: "no", tpt: "Yes (Pro plan)", tptState: "yes" },
  { feature: "UK-specific cost library", tradify: "Price-list management/import", tradifyState: "partial", tpt: "Yes: 31 categories, 462 tasks", tptState: "yes" },
  { feature: "Expert scope validation", tradify: "No", tradifyState: "no", tpt: "Yes", tptState: "yes" },
  { feature: "Labour / materials / plant split", tradify: "No", tradifyState: "no", tpt: "Yes", tptState: "yes" },
  { feature: "Job scheduling and calendar", tradify: "Yes", tradifyState: "yes", tpt: "No", tptState: "no" },
  { feature: "Invoicing", tradify: "Yes", tradifyState: "yes", tpt: "No", tptState: "no" },
  { feature: "Branded PDF quote output", tradify: "Yes", tradifyState: "yes", tpt: "Yes", tptState: "yes" },
  { feature: "Mobile-friendly", tradify: "Yes", tradifyState: "yes", tpt: "Yes", tptState: "yes" },
  { feature: "Free trial length", tradify: "14 days", tradifyState: "partial", tpt: "14 days", tptState: "yes" },
  { feature: "Starting price", tradify: "Lite £34/user/mo", tradifyState: "partial", tpt: "£29/mo (per account)", tptState: "yes" },
  { feature: "Credit card required to trial", tradify: "Not stated", tradifyState: "partial", tpt: "No", tptState: "yes" },
];

function Cell({ value, state }: { value: string; state: TriState }) {
  if (state === "yes") return (
    <span className="flex items-start gap-1.5 text-green-600 dark:text-green-400 font-medium">
      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  if (state === "no") return (
    <span className="flex items-start gap-1.5 text-red-500 dark:text-red-400">
      <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  return (
    <span className="flex items-start gap-1.5 text-amber-600 dark:text-amber-400">
      <Minus className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
}

const SWITCH_REASONS = [
  "You spend too long writing quotes manually in Tradify. The Proper Tool generates a fully itemised scope from a description or drawings in under 10 minutes.",
  "Your jobs involve architect's drawings or structural plans. The Proper Tool reads them and uses dimensions and beam details to produce a more accurate quote.",
  "You need a UK-specific cost library: The Proper Tool has 462 rated tasks across 31 UK residential trade categories. Tradify's cost data is not tailored to the UK market.",
  "You want expert validation on your quotes. The Proper Tool flags scope gaps, pricing outliers, and missing dependencies before you send.",
  "You want a 14-day free trial with no credit card required.",
  "You want per-account pricing, not per-seat. The Proper Tool charges per account regardless of how many people use it.",
];

const FAQS = [
  {
    q: "What is the best Tradify alternative?",
    a: "The best Tradify alternative depends on what you need. If your main requirement is faster, more accurate quoting from drawings or descriptions, rather than job management and scheduling, The Proper Tool is the better fit. It uses AI to generate fully itemised quotes, includes a 31-category UK cost library, and starts at £29/month with a 14-day free trial.",
  },
  {
    q: "Is The Proper Tool better than Tradify for UK builders?",
    a: "For quoting and estimating specifically, yes. The Proper Tool is built around the UK residential construction market with a 31-category UK cost library, AI quote generation from drawings and descriptions, and expert scope validation. Tradify has a broader feature set including job scheduling and invoicing, but its quoting module has no AI scope generation or drawing analysis.",
  },
  {
    q: "Does Tradify read structural drawings?",
    a: "No. Tradify does not analyse structural drawings or architect's plans. The Proper Tool can read uploaded PDFs of drawings, extracting beam sizes, spans, floor areas, and dimensions to generate a more accurate quote. This is a key differentiator for builders working from architect's drawings on extension and conversion projects.",
  },
  {
    q: "How does Tradify pricing compare to The Proper Tool?",
    a: "Tradify lists Lite at £34, Pro at £37 and Plus at £44 per user/month, with a 14-day free trial. The Proper Tool is priced per account, starts at £29/month and offers a 14-day free trial.",
  },
  {
    q: "Can I switch from Tradify to The Proper Tool?",
    a: "Yes. The Proper Tool has a 14-day free trial with no credit card required, so you can run it alongside your existing tools before committing. Setup takes minutes with no long onboarding or data migration required to start generating quotes.",
  },
];

export default function TradifyAlternativePage() {

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Tradify Alternative for UK Builders | The Proper Tool"
        description="Looking for a Tradify alternative? Compare verified Tradify pricing, mobile apps, AI limits, drawing takeoff and trial terms with The Proper Tool for UK builders."
        canonical="/tradify-alternative"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Tradify Alternative</span>
            </div>
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">Comparison</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Tradify Alternative
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Looking for a Tradify alternative that quotes faster and more accurately? The Proper Tool uses AI to generate fully itemised quotes from drawings or a description, with a 31-category UK cost library and expert scope validation built in.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no credit card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="#comparison-table">See the comparison</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · From £29/mo · No credit card required</p>
          </div>
        </section>

        {/* Why people look for a Tradify alternative */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Why builders look for a Tradify alternative</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Tradify is a solid all-in-one job management tool for tradespeople, handling quoting, invoicing, scheduling, and customer management in one place. But it has limitations that matter for UK residential builders who do a high volume of quotes or work from architect's drawings:
            </p>
            <div className="space-y-4">
              {[
                { title: "No AI quote generation", desc: "Tradify's quoting module requires you to manually add line items. There's no AI that reads a description or photo and generates a fully itemised scope for you. For complex residential jobs with 20–30 line items, that's a significant amount of manual work per quote." },
                { title: "No structural drawing analysis", desc: "Tradify cannot read architect's drawings or structural plans. Builders who regularly work from drawings (on extensions, loft conversions, and structural alterations) can't use Tradify to extract dimensions and quantities automatically." },
                { title: "Limited UK-specific cost data", desc: "Tradify's cost library is not built around the UK residential construction market. Builders working in the UK find they need to build and maintain their own rate database, which defeats the purpose of using software." },
                { title: "Per-seat pricing adds up", desc: "Tradify charges per user. For a growing small firm, per-seat pricing increases the monthly cost significantly. The Proper Tool charges per account, regardless of how many people use it." },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">{item.title}: </span>
                    <span className="text-muted-foreground">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature comparison table */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="comparison-table">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">The Proper Tool vs Tradify: feature comparison</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A factual side-by-side comparison of features and pricing.
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-5 py-4 font-semibold text-sm w-2/5">Feature</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%]">Tradify</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%] text-primary">The Proper Tool</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-muted/20">
                      <td className="px-5 py-4 text-sm font-medium">{row.feature}</td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.tradify} state={row.tradifyState} /></td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.tpt} state={row.tptState} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-4 max-w-2xl">
               Competitor pricing and feature information was checked on 19 September 2026. Prices and features may have changed. Verify directly with Tradify before making a purchasing decision.
            </p>
          </div>
        </section>

        {/* Pricing comparison */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Pricing comparison</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Tradify lists Lite at £34, Pro at £37, and Plus at £44 per user/month. It advertises a 14-day free trial; whether payment details are required should be confirmed with Tradify.
            </p>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              The Proper Tool charges per account (one price regardless of how many team members use it), starting from <strong className="text-foreground">£29/month</strong> on the Lite plan. The trial is 14 days with no credit card required.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-xl border border-border p-6 bg-muted/20">
                <h3 className="font-bold text-lg mb-3">Tradify</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />Lite £34, Pro £37, Plus £44 per user/month</li>
                  <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />14-day free trial</li>
                  <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />Payment details: confirm with Tradify</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />Per-seat pricing increases cost as the team grows</li>
                </ul>
              </div>
              <div className="rounded-xl border border-primary bg-primary/5 p-6">
                <h3 className="font-bold text-lg mb-3 text-primary">The Proper Tool</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />From £29/month (per account)</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />14-day free trial</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />No credit card required</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />No per-seat fees: one price for the whole account</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Why builders switch */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Reasons builders switch from Tradify to The Proper Tool</h2>
            <p className="text-lg text-muted-foreground mb-8">Builders who switch are typically looking for faster, more accurate quoting, not a replacement for Tradify's job management features.</p>
            <div className="space-y-4">
              {SWITCH_REASONS.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our verdict */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Our verdict</h2>
            <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                Tradify and The Proper Tool serve different primary needs. Tradify is an all-in-one field service management platform. If you need job scheduling, invoicing, and customer management alongside quoting in a single tool, it's a reasonable choice.
              </p>
              <p>
                <strong className="text-foreground">If your main bottleneck is quoting</strong> (spending too long pricing jobs, losing work because quotes are slow, or under-pricing because you miss scope items), The Proper Tool is the better fit. It's purpose-built for the UK residential construction market and uses AI to generate accurate, fully itemised quotes from drawings or descriptions in under 10 minutes. No manual line-item building. No rate database to maintain.
              </p>
              <p>
                Some builders use The Proper Tool for quoting and Tradify (or another tool) for job management. The two tools don't overlap significantly, so running them alongside each other is a practical option.
              </p>
            </div>
          </div>
        </section>

        {/* CTA box */}
        <section className="py-16 sm:py-20 bg-primary/5 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Try The Proper Tool free for 14 days</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Generate a professional, fully itemised construction quote from drawings or a description in under 10 minutes. No credit card required to get started.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start free, no credit card needed
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · Cancel any time</p>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Disclaimer:</strong> Competitor pricing and feature information on this page is based on publicly available data and is correct to the best of our knowledge at the time of publication. Prices and features may have changed. We recommend verifying current details directly with Tradify before making a purchasing decision.
            </p>
          </div>
        </section>

        {/* Related guides */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related guides</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/best-quoting-software", label: "Best Quoting Software for Builders: 2025 Comparison" },
                { href: "/comparisons", label: "All Software Comparisons" },
                { href: "/quoting-software-for-builders", label: "Quoting Software for Builders" },
                { href: "/job-quoting-software", label: "Job Quoting Software" },
                { href: "/quoting-software-for-contractors", label: "Quoting Software for Contractors" },
                { href: "/tradesman-quoting-tool", label: "Tradesman Quoting Tool" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
